import { RefundProtectQuoteRequest } from 'api/insurance'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { getFlexibleCancellationDays, isFlexibleCancellationPolicy, isWithinSevenDays } from 'lib/offer/cancellationPolicyUtils'
import moment from 'moment'

interface CancellationPolicyInfo {
  cancellationType: 'free' | 'flexible' | undefined
  cancelUntilFullRefundDate: moment.Moment
  cancelAfterNonRefundableDate: moment.Moment | undefined
}

export const getBookingProtectionQuoteKey = (quotePayload: RefundProtectQuoteRequest | null): string => {
  if (!quotePayload) {
    return 'default'
  }

  return Object.keys(quotePayload).reduce((acc, key) => {
    return `${acc}${key}=${quotePayload[key]};`
  }, '')
}

const showFreeCancellation = (
  checkInDate: string | moment.Moment | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
) => {
  if (!cancellationPolicy) {
    return false
  }
  if (cancellationPolicy.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(21, 'days').isAfter(moment().startOf('day'))
  }
}

const showFlexibleCancellation = (
  checkInDate: string | moment.Moment | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
) => {
  if (!cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy)
    const isWithinChangeOfMind = isWithinSevenDays(moment(), moment(checkInDate), moment())
    return isWithinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

export const getCancellationPolicyInfo = (checkInDate: string | undefined, cancellationPolicy: App.OfferCancellationPolicy | undefined): CancellationPolicyInfo => {
  const freeCancellation = showFreeCancellation(checkInDate, cancellationPolicy)
  const flexibleCancellation = showFlexibleCancellation(checkInDate, cancellationPolicy)
  const cancellationType = freeCancellation ? 'free' : flexibleCancellation ? 'flexible' : undefined
  let startBasedOnToday: moment.Moment = moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let cancelUntilFullRefundDate: moment.Moment = moment()
  let cancelAfterNonRefundableDate: moment.Moment | undefined

  if (freeCancellation) {
    const startBasedOnCheckIn = checkInDate && moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    if (startBasedOnCheckIn) {
      cancelUntilFullRefundDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
    }
  } else if (flexibleCancellation && cancellationPolicy && checkInDate) {
    const days = getFlexibleCancellationDays(cancellationPolicy)
    let startBasedOnCheckIn = moment(checkInDate).subtract(days, 'days')

    const changeOfMindDeadline = moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

    const cancellationAfterChangeOfMindPeriod = startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
    startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

    cancelUntilFullRefundDate = startBasedOnToday
    cancelAfterNonRefundableDate = startBasedOnCheckIn
  }

  return {
    cancellationType, cancelUntilFullRefundDate, cancelAfterNonRefundableDate,
  }
}
